import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCart } from "../Cart/CartContext";
import styles from './PricingOptions.module.css';
import logo from "../../media/acadLogoNoBG.png";
import paypalLogo from "../../media/paypal-logo-white.png";

interface PricingPlan {
  header: string;
  subheader: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  currency: string;
  features: string[];
  button: string;
}

export function PricingPlans() {
  const { t } = useTranslation();
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly');
  const { openCart } = useCart();

  const founderPlan = {
    name: t('pricing.founder.name'),
    description: t('pricing.founder.description'),
    price: "40,00",
    currency: "€",
    features: [
      t('pricing.founder.features.lifetime'),
      t('pricing.founder.features.updates'),
      t('pricing.founder.features.limited')
    ],
    cta: t('pricing.founder.cta')
  };

  const dynamicPricingPlans: PricingPlan[] = [
    {
      header: t('pricing.personal.name'),
      subheader: t('pricing.personal.description'),
      priceMonthly: 10.98,
      priceYearly: 5.98,
      currency: "€",
      features: [
        t('pricing.personal.features.academy'),
        t('pricing.personal.features.feedback'),
        t('pricing.personal.features.track'),
        t('pricing.personal.features.setup')
      ],
      button: t('pricing.personal.cta')
    },
    {
      header: t('pricing.team.name'),
      subheader: t('pricing.team.description'),
      priceMonthly: 27.98,
      priceYearly: 14.98,
      currency: "€",
      features: [
        t('pricing.team.features.licenses'),
        t('pricing.team.features.personal'),
        t('pricing.team.features.events'),
        t('pricing.team.features.goals')
      ],
      button: t('pricing.team.cta')
    },
    {
      header: t('pricing.enterprise.name'),
      subheader: t('pricing.enterprise.description'),
      priceMonthly: null,
      priceYearly: null,
      currency: "€",
      features: [
        t('pricing.enterprise.features.licenses'),
        t('pricing.enterprise.features.api'),
        t('pricing.enterprise.features.support'),
        t('pricing.enterprise.features.coaching')
      ],
      button: t('pricing.enterprise.cta')
    }
  ];

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>
        <Trans i18nKey="pricing.title" />
      </h2>

      {/* Founder's Edition Card */}
      <div className={styles.founderContainer}>
        <div className={styles.founderHeader}>
          <span className={styles.founderHeaderText}>
            <Trans i18nKey="pricing.founder.label" />
          </span>
        </div>
        
        <div className={styles.founderContent} onClick={openCart}>
          {/* Logo background */}
          <div className={styles.logoBackground}>
            <img src={logo} alt="Logo background" className={styles.logoImage} />
          </div>
          
          {/* Left section with title and features */}
          <div className={styles.leftSection}>
            <div className={styles.mainTitle}>
              <span className={styles.mainTitleName}>
                {founderPlan.name}
              </span>
              <span className={styles.mainTitleAction}>
                <Trans i18nKey="pricing.action" />
              </span>
            </div>
            
            <div className={styles.divider}></div>
            
            <div className={styles.featuresContainer}>
              {founderPlan.features.map((feature, index) => (
                <div key={index} className={styles.featureItem}>
                  <div className={styles.checkmark}>
                    <svg width="24" height="18" viewBox="0 0 26 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.20535 18.7678L0.417847 9.98029L2.61472 7.78342L9.20535 14.374L23.3501 0.229248L25.547 2.42612L9.20535 18.7678Z" fill="#242424"/>
                    </svg>
                  </div>
                  <span className={styles.featureText}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right section with price and button */}
          <div className={styles.rightSection}>
            <div className={styles.priceBox}>
              <span className={styles.priceText}>
                {founderPlan.price}{founderPlan.currency}
              </span>
              <span className={styles.vatText}>
                <Trans i18nKey="pricing.vat" />
              </span>
            </div>
            
            <button 
              className={styles.checkoutButton} 
              onClick={openCart}
            >
              <div className={styles.paypalButtonContent}>
                <span className={styles.payButtonText}>
                  {founderPlan.cta}
                </span>
                <img src={paypalLogo} alt="PayPal" className={styles.paypalLogo} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className={styles.billingToggleContainer}>
        <div className={styles.billingToggle}>
          <span className={billingInterval === 'monthly' ? styles.billingLabelActive : styles.billingLabel}>
            <Trans i18nKey="pricing.billing.monthlyTitle" />
          </span>
          <button
            onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
            className={styles.toggle}
            aria-label={t('pricing.billing.toggle')}
          >
            <div 
              className={`${styles.toggleKnob} ${billingInterval === 'yearly' ? styles.toggleKnobActive : ''}`} 
            />
          </button>
          <span className={billingInterval === 'yearly' ? styles.billingLabelActive : styles.billingLabel}>
            <Trans i18nKey="pricing.billing.yearlyTitle" />
          </span>
        </div>

        {billingInterval === 'yearly' && (
          <div className={styles.savingsNote}>
            <Trans i18nKey="pricing.billing.savings" />
          </div>
        )}
      </div>

      {/* Regular Plans Grid */}
      <div className={styles.plansGridContainer}>
        {/* Single Coming Soon overlay for monthly plans */}
        <div className={styles.globalComingSoonOverlay}>
          <div className={styles.comingSoonText}>Coming Soon</div>
        </div>
        
        
        <div className={styles.plansGrid}>
          {dynamicPricingPlans.map((plan, index) => (
            <div key={index} className={styles.planCard}>
              <h3 className={styles.planTitle}>{plan.header}</h3>
              <p className={styles.planDescription}>{plan.subheader}</p>
              
              <div className={styles.priceContainer}>
                <div className={styles.planPrice}>
                  {plan.priceMonthly === null || plan.priceYearly === null ? (
                    <>
                      <Trans i18nKey="pricing.enterprise.contact" />
                      <span className={styles.emoticon}>☜(ﾟヮﾟ☜)</span>
                    </>
                  ) : billingInterval === 'monthly' ? 
                      `${plan.priceMonthly.toFixed(2).replace('.', ',')}${plan.currency}` :
                      `${plan.priceYearly.toFixed(2).replace('.', ',')}${plan.currency}`
                  }
                </div>
                {plan.priceMonthly !== null && (
                  <div className={styles.planInterval}>
                    /{t(`pricing.billing.${billingInterval}`)}
                  </div>
                )}
              </div>

              <button 
                className={styles.planButton}
                onClick={openCart}
                disabled={billingInterval === 'monthly'}
              >
                {plan.button}
              </button>

              <ul className={styles.featureList}>
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className={styles.featureListItem}>
                    <span className={styles.featureCheckmark}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PricingPlans;